import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { usePopper } from 'react-popper';
import PropTypes from 'prop-types';

import Tooltip from '@blocks/ui/tooltip';
import { toBoolean } from '@blocks/utils';

import { DOMAIN_DEFAULT } from '../../constants';
import providersDefault from '../../constants/auth-providers';
import { useOAuth2 } from '../../hooks';
import { Button, Modal } from '../../ui';
import { Spinner } from '../../ui/icon';

import AppleIcon from './apple.inline.svg';
import ArrowIcon from './arrow.inline.svg';
import OAuth2ErrorModal from './error';
import Gdpr from './gdpr';
import GoogleIcon from './google.inline.svg';
import {
  ArrowIconApple,
  ArrowIconGoogle,
  Divider,
  PolicyButtonContainer,
  PolicyButtonWrapper,
  PolicyModalContent,
  ProviderButton,
  ProviderButtonText,
  ProviderContainer,
  ProviderUsedContainer,
  ProviderUsedText,
  SocialAuthButtonSpin,
  SocialAuthSubmitSpin,
  UsedProviderRow,
} from './oauth2.ui';

import '../../styles/oauth2.css';
import '../../styles/global.css';

function isPromise(p) {
  if (typeof p === 'object' && typeof p.then === 'function') {
    return true;
  }

  return false;
}

const providerIcons = {
  google: <GoogleIcon />,
  apple: <AppleIcon />,
};

const ProviderUsedGoogle = ({ size }) => {
  return (
    <ProviderUsedContainer>
      <ArrowIconGoogle isSmall={size === 'sm'}>
        <ArrowIcon />
      </ArrowIconGoogle>
      <ProviderUsedText>You previously used this method</ProviderUsedText>
    </ProviderUsedContainer>
  );
};

const ProviderUsedApple = ({ size }) => (
  <ProviderUsedContainer>
    <ProviderUsedText>You previously used this method</ProviderUsedText>
    <ArrowIconApple isSmall={size === 'sm'}>
      <ArrowIcon />
    </ArrowIconApple>
  </ProviderUsedContainer>
);

const OAuth2 = ({
  domain = DOMAIN_DEFAULT,
  source_context = 'authorization',
  providers = providersDefault,
  handler = () => {},
  policyModalTitle = '',
  horizontalLayout = false,
  policyModalSubmit = 'Login',
  isPolicyRequired = false,
  size = 'default',
  debugOAuth = false,
  debugOAuthProviderUsed = '',
  hideDivider = false,
  disableUsedProvider = false,
  placeholder = null,
  enableOAuthInitializationPreview = false,
}) => {
  const modal = useRef(null);
  const [providerTmp, setProviderTmp] = useState('');
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [tooltipActive, setTooltipActive] = useState('');
  const [arrowElement, setArrowElement] = useState(null);

  const { isOAuth2Available, availableOAuth2Providers, usedOAuth2Provider, submitHandler } =
    useOAuth2({
      domain,
      handler,
      source_context,
      providers,
    });

  const isOAuth2AvailableSingle = availableOAuth2Providers.length === 1;
  const placement = isOAuth2AvailableSingle
    ? 'top'
    : tooltipActive === 'google'
    ? 'top-start'
    : 'top-end';

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  });
  const [loading, setLoading] = useState(
    providers.reduce((acc, { provider }) => ({ ...acc, [provider]: false }), {}),
  );
  const isSmallSize = size === 'sm';

  const handleTooltipHover = (event, provider) => {
    setTooltipActive(provider);
    setReferenceElement(event.target);
  };

  const { mutators } = useForm();
  const { values } = useFormState();

  const providerButtonHandler = provider => {
    if (isPolicyRequired && !values.policy_agree) {
      setProviderTmp(provider);
      modal.current.open();
    } else {
      setLoading({ ...loading, [provider]: true });
      const handler = submitHandler(provider, values);
      if (isPromise(handler)) {
        handler.finally(() => {
          setLoading({ ...loading, [provider]: false });
        });
      }
    }
  };

  useEffect(() => {
    if (mutators.togglePolicy && usedOAuth2Provider && !disableUsedProvider) {
      mutators.togglePolicy(true);
    }
  }, [usedOAuth2Provider, mutators.togglePolicy]);

  const getProviderContent = ({ provider, text }) => {
    if (isSmallSize) {
      return loading[provider] ? (
        <SocialAuthButtonSpin>
          <Spinner
            width="18"
            height="18"
          />
        </SocialAuthButtonSpin>
      ) : (
        providerIcons[provider]
      );
    }

    return (
      <>
        {providerIcons[provider]}
        <ProviderButtonText>
          {loading[provider] ? (
            <SocialAuthButtonSpin>
              <Spinner
                width="18"
                height="18"
              />
            </SocialAuthButtonSpin>
          ) : (
            `${availableOAuth2Providers.length === 1 ? 'Continue with ' : ''}${text}`
          )}
        </ProviderButtonText>
      </>
    );
  };

  const isGoogleUsed = useMemo(
    () => usedOAuth2Provider === 'google' || debugOAuthProviderUsed === 'google',
    [usedOAuth2Provider, debugOAuthProviderUsed],
  );

  const isAppleUsed = useMemo(
    () => usedOAuth2Provider === 'apple' || debugOAuthProviderUsed === 'apple',
    [usedOAuth2Provider, debugOAuthProviderUsed],
  );

  if (!isOAuth2Available && toBoolean(enableOAuthInitializationPreview)) {
    return (
      <div style={{ flex: size === 'sm' ? 'initial' : undefined }}>
        <ProviderContainer isSmall={isSmallSize}>
          {providers.map(({ provider, text }) => (
            <ProviderButton
              key={provider}
              type="button"
              isSmall={isSmallSize}
            >
              {getProviderContent({ provider, text })}
            </ProviderButton>
          ))}
        </ProviderContainer>
        {!disableUsedProvider && (
          <>
            {(isGoogleUsed || isAppleUsed) && horizontalLayout && (
              <UsedProviderRow
                second={usedOAuth2Provider === 'apple' || debugOAuthProviderUsed === 'apple'}
              >
                <ProviderUsedGoogle size="sm" />
              </UsedProviderRow>
            )}
            {isGoogleUsed && !horizontalLayout && <ProviderUsedGoogle size={size} />}

            {isAppleUsed && !horizontalLayout && <ProviderUsedApple size={size} />}
          </>
        )}
        {!isSmallSize && !hideDivider && <Divider>or</Divider>}
      </div>
    );
  }

  return isOAuth2Available || debugOAuth ? (
    <div style={{ flex: size === 'sm' ? 'initial' : undefined }}>
      <ProviderContainer isSmall={isSmallSize}>
        {providers.map(
          ({ provider, text, tooltipText }) =>
            (availableOAuth2Providers.includes(provider) || debugOAuth) && (
              <React.Fragment key={provider}>
                <ProviderButton
                  isUsed={
                    disableUsedProvider
                      ? false
                      : provider === usedOAuth2Provider ||
                        (debugOAuthProviderUsed && debugOAuthProviderUsed === provider)
                  }
                  type="button"
                  isSmall={isSmallSize}
                  onClick={() => providerButtonHandler(provider)}
                  onFocus={e => handleTooltipHover(e, provider)}
                  onBlur={() => setTooltipActive('')}
                  onMouseEnter={e => handleTooltipHover(e, provider)}
                  onMouseLeave={() => setTooltipActive('')}
                >
                  {getProviderContent({ provider, text })}
                </ProviderButton>
                <Tooltip
                  tooltipRef={setPopperElement}
                  style={styles.popper}
                  isActive={tooltipActive === provider}
                  arrowProps={{
                    style: styles.arrow,
                    ref: setArrowElement,
                  }}
                  {...attributes.popper}
                >
                  {tooltipText}
                </Tooltip>
              </React.Fragment>
            ),
        )}
      </ProviderContainer>
      {!disableUsedProvider && (
        <>
          {(isGoogleUsed || isAppleUsed) && horizontalLayout && (
            <UsedProviderRow
              second={usedOAuth2Provider === 'apple' || debugOAuthProviderUsed === 'apple'}
            >
              <ProviderUsedGoogle size="sm" />
            </UsedProviderRow>
          )}
          {isGoogleUsed && !horizontalLayout && <ProviderUsedGoogle size={size} />}

          {isAppleUsed && !horizontalLayout && <ProviderUsedApple size={size} />}
        </>
      )}

      <OAuth2ErrorModal />

      <Modal
        ref={modal}
        title={policyModalTitle}
      >
        <PolicyModalContent>
          <Gdpr />

          <PolicyButtonContainer>
            <PolicyButtonWrapper>
              <Button
                type="button"
                onClick={() => {
                  setLoading({ ...loading, [providerTmp]: true });
                  const handler = submitHandler(providerTmp, values);
                  if (isPromise(handler)) {
                    handler.finally(() => setLoading({ ...loading, [providerTmp]: false }));
                  }
                }}
                disabled={!values.policy_agree || Object.values(loading).includes(true)}
              >
                {Object.values(loading).includes(true) ? (
                  <SocialAuthSubmitSpin>
                    <Spinner
                      width="18"
                      height="18"
                    />
                  </SocialAuthSubmitSpin>
                ) : (
                  policyModalSubmit
                )}
              </Button>
            </PolicyButtonWrapper>
          </PolicyButtonContainer>
        </PolicyModalContent>
      </Modal>

      {!isSmallSize && !hideDivider && <Divider>or</Divider>}
    </div>
  ) : (
    placeholder
  );
};

OAuth2.propTypes = {
  domain: PropTypes.string,
  source_context: PropTypes.string,
  placeholder: PropTypes.node,
  providers: PropTypes.arrayOf(
    PropTypes.shape({
      provider: PropTypes.string,
      text: PropTypes.string,
      tooltipText: PropTypes.string,
    }),
  ),
  handler: PropTypes.func.isRequired,
  isPolicyRequired: PropTypes.bool,
  policyModalTitle: PropTypes.string,
  policyModalSubmit: PropTypes.string,
  size: PropTypes.oneOf(['default', 'sm']),
  debugOAuth: PropTypes.bool,
  debugOAuthProviderUsed: PropTypes.string,
  horizontalLayout: PropTypes.bool,
  hideDivider: PropTypes.bool,
  disableUsedProvider: PropTypes.bool,
  enableOAuthInitializationPreview: PropTypes.bool,
};

export default OAuth2;
